import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { supabase, apiFetch, withRetry, withTimeout } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

const DataContext = createContext(null);

const ORDER_STATUSES = [
  'Received', 'Confirmed', 'Queued', 'In Production', 'Blocked',
  'QC', 'Ready', 'Out for Delivery', 'Delivered', 'Failed Delivery', 'Cancelled',
];

async function safeSupabaseSelect(builder, { label, timeoutMs = 8000 }) {
  const { data, error } = await withTimeout(builder, timeoutMs, label);
  if (error) throw error;
  return data;
}

export function DataProvider({ children }) {
  const { role, session } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loadErrors, setLoadErrors] = useState({});
  const [retryingKeys, setRetryingKeys] = useState(new Set());

  const lastGoodRef = useRef({
    customers: [], products: [], quotations: [], orders: [], deliveries: [], designers: [], drivers: [],
  });

  const mark = (key, err) => setLoadErrors(prev => {
    const next = { ...prev };
    if (err) next[key] = err.message || String(err);
    else delete next[key];
    return next;
  });

  const setRetrying = (key, on) => setRetryingKeys(prev => {
    const next = new Set(prev);
    if (on) next.add(key); else next.delete(key);
    return next;
  });

  const runReload = async (key, fn, { after, onError, setState }) => {
    setRetrying(key, true);
    try {
      const data = await withRetry(fn, { attempts: 3, label: key });
      lastGoodRef.current[key] = data;
      setState(data);
      mark(key, null);
      if (after) after(data);
    } catch (e) {
      console.warn(`[data] ${key} permanently failed; keeping last-good snapshot`, e);
      setState(lastGoodRef.current[key]);
      if (onError) onError();
      mark(key, e);
    } finally {
      setRetrying(key, false);
    }
  };

  const reloadOrders = useCallback(async () => {
    if (!session) return;
    await runReload(
      'orders',
      () => apiFetch('/orders/full', { timeoutMs: 10000 }).then(d => Array.isArray(d) ? d : []),
      { setState: setOrders },
    );
  }, [session]);

  const reloadDeliveries = useCallback(async () => {
    if (!session) return;
    await runReload(
      'deliveries',
      () => safeSupabaseSelect(
        supabase.from('deliveries').select('id, order_id, driver_id, status, window_start, window_end, delivery_instructions, recipient_name, delivered_at, failed_reason, photo_path').order('created_at', { ascending: false }),
        { label: 'deliveries.select' },
      ).then(d => d || []),
      { setState: setDeliveries },
    );
  }, [session]);

  const reloadCustomers = useCallback(async () => {
    if (!session) return;
    await runReload(
      'customers',
      () => safeSupabaseSelect(
        supabase.from('customers').select('*').order('name'),
        { label: 'customers.select' },
      ).then(d => d || []),
      { setState: setCustomers },
    );
  }, [session]);

  const reloadProducts = useCallback(async () => {
    if (!session) return;
    await runReload(
      'products',
      () => safeSupabaseSelect(
        supabase.from('products').select('*').order('name'),
        { label: 'products.select' },
      ).then(d => d || []),
      { setState: setProducts },
    );
  }, [session]);

  const reloadQuotations = useCallback(async () => {
    if (!session) return;
    await runReload(
      'quotations',
      () => safeSupabaseSelect(
        supabase.from('quotations')
          .select('id, number, customer_id, status, internal_notes, created_at, quote_lines(id, item_name, qty, unit_price, unit_cost_snapshot)')
          .order('created_at', { ascending: false }),
        { label: 'quotations.select' },
      ).then(d => d || []),
      { setState: setQuotations },
    );
  }, [session]);

  const reloadStaff = useCallback(async () => {
    if (!session) return;
    await runReload(
      'staff',
      () => safeSupabaseSelect(
        supabase.from('profiles').select('id, email, full_name, role').eq('active', true),
        { label: 'profiles.select' },
      ).then(d => d || []),
      {
        setState: (list) => {
          const ds = list.filter((p) => p.role === 'designer');
          const dr = list.filter((p) => p.role === 'driver');
          lastGoodRef.current.designers = ds;
          lastGoodRef.current.drivers = dr;
          setDesigners(ds); setDrivers(dr);
        },
      },
    );
  }, [session]);

  useEffect(() => {
    if (!session) return;
    reloadOrders(); reloadDeliveries(); reloadStaff();
    if (['admin', 'manager', 'sales'].includes(role)) { reloadCustomers(); reloadQuotations(); }
    reloadProducts();
  }, [session, role, reloadOrders, reloadDeliveries, reloadCustomers, reloadProducts, reloadQuotations, reloadStaff]);

  useEffect(() => {
    if (!session) return;
    const ch = supabase
      .channel('velvet-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => reloadOrders())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'deliveries' }, () => reloadDeliveries())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [session, reloadOrders, reloadDeliveries]);

  const getCustomer = useCallback((id) => customers.find((c) => c.id === id), [customers]);
  const getProduct = useCallback((id) => products.find((p) => p.id === id), [products]);

  const isRetrying = retryingKeys.size > 0;
  const hasErrors = Object.keys(loadErrors).length > 0;

  const value = useMemo(() => ({
    customers, products, quotations, orders, deliveries, designers, drivers,
    orderStatuses: ORDER_STATUSES,
    getCustomer, getProduct,
    loadErrors, retryingKeys, isRetrying, hasErrors,
    reloadOrders, reloadDeliveries, reloadCustomers, reloadProducts, reloadQuotations, reloadStaff,
  }), [customers, products, quotations, orders, deliveries, designers, drivers, getCustomer, getProduct,
      loadErrors, retryingKeys, isRetrying, hasErrors,
      reloadOrders, reloadDeliveries, reloadCustomers, reloadProducts, reloadQuotations, reloadStaff]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export const useVelvet = () => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useVelvet must be inside DataProvider');
  return ctx;
};
